import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import api, {
  cachedGet, getAccessToken, setAccessToken,
  setAuthExpiry, clearAuthStorage,
  refreshAccessToken,
} from '@/services/api';
import { useWebSocket } from '@/hooks/useWebSocket';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  // `token` is an opaque session marker (NOT the actual JWT — that's in httpOnly cookies).
  // Kept for backward compat: components use `if (token)` to check auth state.
  const [token, setToken] = useState(getAccessToken());
  const [loading, setLoading] = useState(true);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [impersonationData, setImpersonationData] = useState(null);

  useEffect(() => {
    const initAuth = async () => {
      setLoading(true);
      try {
        // Always try /auth/me — httpOnly cookies are sent automatically.
        // No localStorage check needed.
        const loadMe = async () => {
          const response = await api.get('/auth/me', { withCredentials: true });
          const data = response.data;
          if (data && typeof data === 'object' && data.id) {
            setUser(data);
            setAccessToken('active');
            setToken(getAccessToken());
          } else {
            setUser(null);
            setToken(null);
          }
        };

        try {
          await loadMe();
        } catch (error) {
          if (error.response?.status === 401) {
            const refreshed = await refreshAccessToken();
            if (refreshed) {
              setToken(getAccessToken());
              try {
                await loadMe();
              } catch (e2) {
                if (e2.response?.status === 401) {
                  clearAuthStorage();
                  setToken(null);
                  setUser(null);
                } else {
                  console.error('Auth /auth/me failed after refresh:', e2);
                }
              }
            } else {
              clearAuthStorage();
              setToken(null);
              setUser(null);
            }
          } else if (error.response) {
            console.error('Auth /auth/me HTTP error:', error.response?.status, error);
            setUser(null);
          } else {
            console.error('Auth check failed (network):', error);
            await new Promise((r) => setTimeout(r, 400));
            try {
              await loadMe();
            } catch (e2) {
              console.error('Auth retry failed:', e2);
              if (e2.response?.status === 401) {
                clearAuthStorage();
                setToken(null);
                setUser(null);
              }
            }
          }
        }

        // Check impersonation state from non-secret flag
        const isImp = sessionStorage.getItem('is_impersonating');
        if (isImp) {
          setIsImpersonating(true);
          setImpersonationData({ active: true });
        }
      } finally {
        setLoading(false);
      }
    };
    initAuth();
  }, []);

  const login = useCallback(async (email, password) => {
    const response = await api.post('/auth/login', { email, password }, { withCredentials: true });
    // Backend sets httpOnly cookies — we do NOT read tokens from the JSON body.
    const userData = response.data?.user;
    const expiresIn = response.data?.expires_in;
    if (!userData) {
      throw new Error('Login response missing user data');
    }
    setAuthExpiry(expiresIn);
    setAccessToken('active');
    setToken(getAccessToken());
    setUser(userData);

    // Demo-mode preload: pre-warm common pages after login so navigation feels instant.
    try {
      if (userData?.role !== 'super_admin') {
        setTimeout(() => {
          const did = userData?.dealership_id;
          Promise.all([
            cachedGet('/dashboard/full', 30000),
            cachedGet('/conversations', { ttl: 30000, params: { page: 1, page_size: 20 } }),
            cachedGet('/contacts', { ttl: 30000, params: { page: 1, page_size: 20 } }),
            cachedGet('/pipeline/grouped', 30000),
            did ? cachedGet(`/leaderboard/${did}?period=month&type=basic`, 60000) : Promise.resolve(null),
            cachedGet('/inventory/paged', { ttl: 60000, params: { page: 1, page_size: 24 } }),
          ]).catch(() => {});
        }, 100);
      }
    } catch {
      // ignore preload errors
    }
    return userData;
  }, []);

  const changePassword = useCallback(async (currentPassword, newPassword) => {
    await api.post('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    }, { withCredentials: true });
    const me = await api.get('/auth/me', { withCredentials: true });
    setUser(me.data);
    return me.data;
  }, []);

  const register = useCallback(async (email, password, name) => {
    const response = await api.post('/auth/register', { email, password, name }, { withCredentials: true });
    // Backend sets httpOnly cookies — we do NOT read tokens from the JSON body.
    const userData = response.data?.user;
    const expiresIn = response.data?.expires_in;
    setAuthExpiry(expiresIn);
    setAccessToken('active');
    setToken(getAccessToken());
    setUser(userData);
    return userData;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout', {}, { withCredentials: true });
    } catch {
      /* ignore */
    }
    clearAuthStorage();
    sessionStorage.removeItem('is_impersonating');
    setToken(null);
    setUser(null);
    setIsImpersonating(false);
    setImpersonationData(null);
  }, []);

  const startImpersonation = useCallback(async (impersonationToken, metadata) => {
    // Backend already set httpOnly cookies during the impersonate API call.
    // We only store non-secret metadata in sessionStorage.
    sessionStorage.setItem('is_impersonating', 'true');
    window.location.href = metadata?.dealer_url || '/dashboard';
  }, []);

  const exitImpersonation = useCallback(async () => {
    sessionStorage.removeItem('is_impersonating');
    try {
      // Backend restores admin cookies and clears impersonation state
      await api.post('/admin/demos/end-impersonation', {}, { withCredentials: true });
    } catch { /* ignore */ }
    window.location.href = '/admin/demos';
  }, []);

  const getAuthHeaders = useCallback(() => ({}), []);

  const isSuperAdmin = useCallback(() => user?.role === 'super_admin', [user?.role]);

  const { subscribe: wsSubscribe, connected: wsConnected } = useWebSocket(token);
  const contextValue = useMemo(() => ({
    user,
    token,
    loading,
    login,
    register,
    logout,
    changePassword,
    getAuthHeaders,
    isSuperAdmin,
    wsSubscribe,
    wsConnected,
    isImpersonating,
    impersonationData,
    startImpersonation,
    exitImpersonation,
  }), [
    user,
    token,
    loading,
    login,
    register,
    logout,
    changePassword,
    wsSubscribe,
    wsConnected,
    isImpersonating,
    impersonationData,
    getAuthHeaders,
    isSuperAdmin,
    startImpersonation,
    exitImpersonation,
  ]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};
